import React, { CSSProperties, ImgHTMLAttributes, ReactElement, ReactNode } from 'react'

import { getImagePreviewTriggerProps, ZoomHint } from '@/components/ui/zoomable-image'

import { getAssetLink } from './helpers/get-asset-link'

type AvatarSize = 'small' | 'medium' | 'large' | 'big' | string

type Props = {
  children: ReactNode
  size?: AvatarSize
  background?: string
  style?: CSSProperties
  square?: boolean
  // Opens the photo in the preview modal on click; titled with `zoomTitle` (e.g. the person's name)
  zoomable?: boolean
  zoomTitle?: string
}

// type guard: check is it <img />
function isImgElement(node: ReactNode): node is ReactElement<ImgHTMLAttributes<HTMLImageElement>> {
  return React.isValidElement(node) && typeof node.type === 'string' && node.type === 'img'
}

const resolveSrc = (src: unknown) =>
  typeof src === 'string' && src.trim() !== ''
    ? src
    : (getAssetLink({ file: 'avatar.png', path: 'chitown-avatar/avatar' }) ?? undefined)

export default function Avatar({
  background = '',
  children,
  size = 'medium',
  square = false,
  style,
  zoomable = false,
  zoomTitle,
}: Props) {
  const img = React.Children.toArray(children).find(isImgElement)
  const zoomProps =
    zoomable && img
      ? getImagePreviewTriggerProps({
          src: resolveSrc(img.props.src),
          title: zoomTitle ?? 'Avatar',
        })
      : {}

  return (
    <span
      className={`ctp-avatar ctp-avatar--${size}${square ? ' ctp-avatar--square' : ''}`}
      {...zoomProps}
      style={{ backgroundColor: background, ...style, ...zoomProps.style }}
    >
      {React.Children.map(children, (child) => {
        if (isImgElement(child)) {
          const { alt, src, ...rest } = child.props

          return React.cloneElement(child, {
            ...rest,
            alt: alt ?? 'User Avatar',
            src: resolveSrc(src),
          })
        }
        return child
      })}
      {zoomProps.onClick && <ZoomHint size="2rem" />}
    </span>
  )
}
